import type { WsTopic } from "@app/shared";
import { getToken, tokenIsUsable } from "./auth";

// M31b — the topic union is shared with the server, so a topic added there is a compile
// error here until this file gains a case (or deliberately ignores it), not a silent drop.
export type { WsTopic } from "@app/shared";
export type WsEvent = { topic: WsTopic; payload: unknown };
/**
 * `stale` is part of the tick, not something a consumer is trusted to work out. **W4.1.**
 *
 * The store used to hand back a bid and an ask with a timestamp nobody was obliged to read,
 * which meant a quote from before the socket dropped rendered exactly like a live one. §9's
 * rule for the whole app is that stale data must never be shown as current, and the only way
 * to keep that true for the first screen that renders a quote is to make the flag impossible
 * to receive without seeing.
 */
export type QuoteTick = { bid: number; ask: number; at: number; stale: boolean };

/**
 * M31 — one position's last P/L tick. Same staleness contract as QuoteTick, for the same
 * reason: a P/L frozen at signal-loss must never render as live.
 */
export type PositionTick = { profit?: number; currentPrice?: number; at: number; stale: boolean };

type EventListener = (event: WsEvent) => void;
export type ConnectionState = "connecting" | "open" | "closed";
type ConnectionListener = (state: ConnectionState) => void;

const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 30_000;

/**
 * How old a quote may be before it stops counting as live.
 *
 * The server pushes at most one frame per second per symbol (`ws.ts`'s throttle), and the
 * broker's own `quoteStreamingIntervalInSeconds` sits under that — so ten seconds is several
 * missed ticks, not one late one. A quiet symbol going stale between real ticks is the
 * acceptable direction of error here: "I am not sure this is current" costs nothing, and the
 * opposite claim is the one this project cannot afford.
 */
export const QUOTE_TTL_MS = 10_000;

/** How often ages are re-checked. Cheap: one pass over a handful of symbols. */
const QUOTE_SWEEP_MS = 2_000;

/**
 * One websocket connection for the whole app, held outside React so route changes and
 * remounts never spawn a second socket.
 *
 * Frames are advisory (server's own doc comment on `ws.ts`): a dropped frame may delay
 * information but must never lose it, so `useLiveEvents` treats every reconnect as "assume
 * everything might have changed" and does a full query invalidation rather than trying to
 * resume exactly where the last frame left off.
 *
 * Quotes get their own store instead of flowing through React Query: they arrive roughly
 * once a second per symbol, and routing that through the query cache would re-render the
 * whole tree on every tick. `useQuotes` reads this store with `useSyncExternalStore`.
 */
class LiveSocket {
  private socket: WebSocket | null = null;
  /** Read by `useConnectionState` through `useSyncExternalStore`, so it must be a plain
   *  value that only changes when the connection does. */
  private state: ConnectionState = "closed";
  private attempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private closedByUser = true;
  private readonly listeners = new Set<EventListener>();
  private readonly connectionListeners = new Set<ConnectionListener>();
  private readonly quotes = new Map<string, QuoteTick>();
  private readonly quoteListeners = new Map<string, Set<() => void>>();
  private quoteSweep: ReturnType<typeof setInterval> | null = null;
  /**
   * M31 — the position tick book. The snapshot IS the storage: every frame builds a fresh
   * Map, so `getPositionTicks` returns a reference that is stable between frames — the
   * contract useSyncExternalStore renders against.
   */
  private positionTicks: ReadonlyMap<string, PositionTick> = new Map();
  private readonly positionListeners = new Set<() => void>();

  connect(): void {
    if (this.socket || this.reconnectTimer) return;
    const token = getToken();
    // W4.2: an expired token cannot produce a socket — the server refuses the upgrade with a
    // 401, which a browser reports as close code 1006, exactly like a lost connection. Since
    // the close event cannot tell the two apart, the token is checked here instead.
    if (!token || !tokenIsUsable(token)) return;
    this.closedByUser = false;
    this.open(token);
  }

  disconnect(): void {
    this.closedByUser = true;
    this.notifyConnection("closed");
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.socket?.close();
    this.socket = null;
    this.stopQuoteSweep();
    this.markQuotesStale();
  }

  subscribe(listener: EventListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onConnectionChange(listener: ConnectionListener): () => void {
    this.connectionListeners.add(listener);
    return () => this.connectionListeners.delete(listener);
  }

  getQuote(symbol: string): QuoteTick | undefined {
    return this.quotes.get(symbol);
  }

  getPositionTicks(): ReadonlyMap<string, PositionTick> {
    return this.positionTicks;
  }

  subscribePositions(onChange: () => void): () => void {
    this.positionListeners.add(onChange);
    return () => this.positionListeners.delete(onChange);
  }

  subscribeQuote(symbol: string, onChange: () => void): () => void {
    let set = this.quoteListeners.get(symbol);
    if (!set) {
      set = new Set();
      this.quoteListeners.set(symbol, set);
    }
    set.add(onChange);
    return () => set!.delete(onChange);
  }

  private open(token: string): void {
    this.notifyConnection("connecting");
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${protocol}//${window.location.host}/ws?token=${encodeURIComponent(token)}`;
    const socket = new WebSocket(url);
    this.socket = socket;

    socket.addEventListener("open", () => {
      if (this.socket !== socket) return;
      this.attempt = 0;
      this.startQuoteSweep();
      this.notifyConnection("open");
    });

    socket.addEventListener("message", (raw) => {
      if (this.socket !== socket) return;
      let event: WsEvent;
      try {
        event = JSON.parse(raw.data as string) as WsEvent;
      } catch {
        return;
      }
      if (event.topic === "positions") {
        const payload = event.payload as {
          positions?: { id?: unknown; profit?: unknown; currentPrice?: unknown }[];
        };
        if (Array.isArray(payload?.positions)) {
          const next = new Map<string, PositionTick>();
          for (const position of payload.positions) {
            if (typeof position?.id !== "string") continue;
            next.set(position.id, {
              profit: typeof position.profit === "number" ? position.profit : undefined,
              currentPrice: typeof position.currentPrice === "number" ? position.currentPrice : undefined,
              at: Date.now(),
              stale: false,
            });
          }
          this.positionTicks = next;
          this.notifyPositions();
        }
        return;
      }
      if (event.topic?.startsWith("quote:")) {
        const symbol = event.topic.slice("quote:".length);
        const payload = event.payload as { bid?: number; ask?: number };
        if (typeof payload?.bid === "number" && typeof payload?.ask === "number") {
          this.quotes.set(symbol, { bid: payload.bid, ask: payload.ask, at: Date.now(), stale: false });
          this.notifyQuote(symbol);
        }
        return;
      }
      for (const listener of this.listeners) listener(event);
    });

    const scheduleReconnect = () => {
      // A closing socket that is no longer `this.socket` (superseded by a fresh connect(),
      // e.g. logout → login before the old socket's async close event lands) must not touch
      // the current connection's state or schedule a competing reconnect.
      if (this.socket !== socket) return;
      this.socket = null;
      // Immediately, not at the next sweep: a closed socket means no tick is coming, so the
      // age of the last one has stopped being the interesting fact about it.
      this.stopQuoteSweep();
      this.markQuotesStale();
      this.notifyConnection("closed");
      if (this.closedByUser) return;
      const backoff = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** this.attempt);
      const jitter = backoff * (0.7 + Math.random() * 0.6);
      this.attempt += 1;
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        const current = getToken();
        // Re-read rather than reusing the token this socket was opened with: thirty days is
        // long enough for it to have expired mid-session, and retrying against a credential
        // that can never work again is a loop with no exit — the specific W4.2 failure, once
        // every thirty seconds, on a phone. The next query 401s and `api.ts` routes to login.
        if (current && tokenIsUsable(current)) this.open(current);
      }, jitter);
    };

    socket.addEventListener("close", scheduleReconnect);
    socket.addEventListener("error", () => socket.close());
  }

  /**
   * Ages are re-checked on a timer rather than at read time, and that is a requirement
   * rather than a style choice: `useSyncExternalStore` loops forever if `getSnapshot` hands
   * back a freshly built object on every read, so the flag has to live inside a stored value
   * that only changes when something actually changed.
   */
  private startQuoteSweep(): void {
    if (this.quoteSweep) return;
    this.quoteSweep = setInterval(() => this.markQuotesStale(Date.now() - QUOTE_TTL_MS), QUOTE_SWEEP_MS);
  }

  private stopQuoteSweep(): void {
    if (!this.quoteSweep) return;
    clearInterval(this.quoteSweep);
    this.quoteSweep = null;
  }

  /**
   * Flags every quote older than `olderThan` — or all of them, which is what a dropped
   * socket means. The numbers are kept rather than deleted: a value labelled stale can still
   * answer "roughly where was it?", while one that vanishes as you look at it just reads as
   * a broken screen.
   */
  private markQuotesStale(olderThan = Number.POSITIVE_INFINITY): void {
    for (const [symbol, tick] of this.quotes) {
      if (tick.stale || tick.at > olderThan) continue;
      this.quotes.set(symbol, { ...tick, stale: true });
      this.notifyQuote(symbol);
    }
    // M31 — position ticks age on the same clock and die with the same socket.
    if ([...this.positionTicks.values()].some((tick) => !tick.stale && tick.at <= olderThan)) {
      const next = new Map<string, PositionTick>();
      for (const [id, tick] of this.positionTicks) {
        next.set(id, tick.stale || tick.at > olderThan ? tick : { ...tick, stale: true });
      }
      this.positionTicks = next;
      this.notifyPositions();
    }
  }

  private notifyPositions(): void {
    for (const cb of this.positionListeners) cb();
  }

  private notifyQuote(symbol: string): void {
    for (const cb of this.quoteListeners.get(symbol) ?? []) cb();
  }

  get connectionState(): ConnectionState {
    return this.state;
  }

  private notifyConnection(state: ConnectionState): void {
    this.state = state;
    for (const listener of this.connectionListeners) listener(state);
  }
}

export const liveSocket = new LiveSocket();
